import { BarChartIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Flex, Spinner } from '@radix-ui/themes';
import { CommonTableButton } from 'components/common/table/button';
import { CommonTooltip } from 'components/common/tooltip';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { CheckedContext } from 'contexts/layout/checked.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { createSocket } from 'services/socket';
import { useAuthStore } from 'stores/use-auth-store';

const getConnectionButtonLabel = (
  subscribed: boolean,
  loading: boolean
): string => {
  if (loading) {
    return 'Loading';
  }

  if (subscribed) {
    return 'Live';
  }

  return 'Reconnect';
};

const getConnectionButtonColor = (
  subscribed: boolean,
  subscribing: boolean
): RadixColor => {
  if (subscribing) {
    return RADIX.COLOR.NEUTRAL;
  }

  if (subscribed) {
    return RADIX.COLOR.ACCENT;
  }

  return RADIX.COLOR.WARNING;
};

const getConnectionButtonIcon = (
  subscribed: boolean,
  subscribing: boolean
): ReactNode => {
  if (subscribing) {
    return <Spinner />;
  }

  if (subscribed) {
    return <BarChartIcon />;
  }

  return <ExclamationTriangleIcon />;
};

const MachinesSummaryButton = () => {
  const { resetChecked } = useContext(CheckedContext);
  const loadingMachinesAndModels = useMachinesPortalStore((s) => s.loading);
  const subscribing = useMachinesPortalStore((s) => s.subscribing);
  const subscribed = useMachinesPortalStore((s) => s.subscribed);
  const subscriptionLastUpdated = useMachinesPortalStore(
    (s) => s.subscriptionLastUpdated
  );
  const restartSubscription = useMachinesPortalStore(
    (s) => s.restartSubscription
  );

  const loading = loadingMachinesAndModels || subscribing;

  const label = getConnectionButtonLabel(subscribed, loading);
  const color = getConnectionButtonColor(subscribed, loading);
  const icon = getConnectionButtonIcon(subscribed, loading);

  const tooltipText = subscriptionLastUpdated
    ? `Last updated: ${format(parseISO(subscriptionLastUpdated), 'h:mm:ss a', {
        timeZone: LOCAL_TIMEZONE,
      })}`
    : '';

  return (
    <CommonTooltip
      disabled={!subscribed}
      trigger={
        <Flex>
          <CommonTableButton
            color={color}
            icon={icon}
            label={label}
            disabled={loading}
            loading={loading}
            variant="soft"
            className={subscribed ? 'pulse-button-animation' : ''}
            onClick={() => {
              resetChecked();
              restartSubscription();
            }}
          />
        </Flex>
      }
      text={tooltipText}
    />
  );
};

const MachineDetailsButton = () => {
  const loadingMachineDetails = useMachinesPortalStore(
    (s) => s.loadingMachineDetails
  ); // REST request loading state
  const subscribingDetails = useMachinesPortalStore(
    (s) => s.subscribingDetails
  ); // WS subscription loading state
  const subscribedDetails = useMachinesPortalStore((s) => s.subscribedDetails);
  const subscriptionDetailsLastUpdated = useMachinesPortalStore(
    (s) => s.subscriptionDetailsLastUpdated
  );
  const restartDetailsSubscription = useMachinesPortalStore(
    (s) => s.restartDetailsSubscription
  );

  const loading = loadingMachineDetails || subscribingDetails;

  const label = getConnectionButtonLabel(subscribedDetails, loading);
  const color = getConnectionButtonColor(subscribedDetails, loading);
  const icon = getConnectionButtonIcon(subscribedDetails, loading);

  const tooltipText = subscriptionDetailsLastUpdated
    ? `Last updated: ${format(
        parseISO(subscriptionDetailsLastUpdated),
        'h:mm:ss a',
        {
          timeZone: LOCAL_TIMEZONE,
        }
      )}`
    : '';

  return (
    <CommonTooltip
      disabled={!subscribedDetails}
      trigger={
        <Flex>
          <CommonTableButton
            size="1"
            color={color}
            icon={icon}
            label={label}
            disabled={loading}
            loading={loading}
            variant="soft"
            className={subscribedDetails ? 'pulse-button-animation' : ''}
            onClick={() => {
              restartDetailsSubscription();
            }}
          />
        </Flex>
      }
      text={tooltipText}
    />
  );
};

// only checks for WebSocket connection
const MachineConnectionButton = () => {
  const current = useAuthStore((s) => s.current);
  const wsConnected = useAuthStore((s) => s.wsConnected);

  const [loading, setLoading] = useState(false);

  const label = getConnectionButtonLabel(wsConnected, loading);
  const color = getConnectionButtonColor(wsConnected, loading);
  const icon = getConnectionButtonIcon(wsConnected, loading);

  useEffect(() => {
    if (wsConnected) {
      setLoading(false);
    }
  }, [wsConnected]);

  return (
    <Flex>
      <CommonTableButton
        size="1"
        color={color}
        icon={icon}
        label={label}
        disabled={loading}
        loading={loading}
        variant="soft"
        className={wsConnected ? 'pulse-button-animation' : undefined}
        onClick={() => {
          if (wsConnected) {
            return;
          }

          setLoading(true);

          // todo: shouldn't really be necessary if socket.io reconnects automatically
          createSocket(current.token);
        }}
      />
    </Flex>
  );
};

interface IProps {
  type: 'summary' | 'details' | 'connection';
}

export const MachineSubscriptionButton = ({ type }: IProps) => {
  if (type === 'summary') {
    return <MachinesSummaryButton />;
  }

  if (type === 'connection') {
    return <MachineConnectionButton />;
  }

  return <MachineDetailsButton />;
};
